import { BarChart3, ChevronRight, Dumbbell, ListChecks, Play } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '../../lib/api.ts';
import type { RunState, Workout } from '../../lib/planning.ts';
import { etapeFaite, getCurrentRun, listWorkouts } from '../../lib/planning.ts';
import { getCurrent } from '../../lib/training.ts';
import type { Session } from '../../lib/training.ts';

/* Le menu Entraînement.
 *
 * Deux portes, de même rang, et rien qui grandisse entre elles :
 *
 *   — une SÉANCE est un plan écrit à l'avance, qu'on suit sans réfléchir une
 *     fois le seau posé sur le tapis ;
 *   — un EXERCICE est une brique, qu'on lance seul quand on sait déjà ce qu'on
 *     vient faire.
 *
 * La liste des séances vivait ici. À partir de trois ou quatre, elle repoussait
 * « Exercices » sous la ligne de flottaison — et une entrée qu'on ne voit plus
 * est une entrée qui n'existe plus, surtout sur un téléphone tenu d'une main
 * gantée. Ce qui grandit vit donc dans sa propre page ; ce menu ne fait que
 * deux hauteurs de carte, quoi qu'il arrive.
 *
 * Seul ce qui est COMMENCÉ passe devant : rouvrir l'application au practice
 * doit ramener là où on en était, pas proposer de choisir à nouveau.
 */

const dayLabel = (iso: string): string =>
    new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' });

export function TrainingHome() {
    const [workouts, setWorkouts] = useState<Workout[]>([]);
    const [run, setRun] = useState<RunState['run']>(null);
    const [runItems, setRunItems] = useState<RunState['items']>([]);
    const [session, setSession] = useState<Session | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        try {
            const [w, r, cur] = await Promise.all([listWorkouts(), getCurrentRun(), getCurrent()]);
            setWorkouts(w);
            setRun(r.run);
            setRunItems(r.items);
            setSession(cur.session);
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Chargement impossible');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { void load(); }, [load]);

    if (loading) {
        return <div className="flex justify-center py-16"><span className="loading loading-ring loading-lg text-primary" /></div>;
    }

    const done = runItems.filter((i) => i.sessionStatus === 'completed').length;
    const nextItem = runItems.find((i) => !etapeFaite(i));

    return (
        <div className="space-y-4 py-2">
            <h1 className="text-2xl font-bold tracking-tight">Entraînement</h1>

            {error && <div role="alert" className="alert alert-error text-sm"><span>{error}</span></div>}

            {/* La reprise passe avant tout le reste. Un joueur qui rouvre
                l'application au practice a déjà commencé : lui reproposer le
                catalogue serait lui demander de retrouver où il en était. */}
            {run && (
                <section className="rounded-box border-2 border-primary bg-primary/10 p-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-primary">
                        Séance en cours
                    </p>
                    <h2 className="mt-1 text-lg font-bold">{run.name}</h2>
                    <p className="mt-1 text-sm opacity-70">
                        {done} exercice{done > 1 ? 's' : ''} sur {runItems.length} terminé{done > 1 ? 's' : ''}
                        {nextItem && ` · au suivant : ${nextItem.exerciseName ?? 'exercice'}`}
                    </p>
                    <progress className="progress progress-primary mt-2 w-full"
                        value={done} max={Math.max(1, runItems.length)} />
                    <Link to="/entrainement/seance-en-cours"
                        className="btn btn-primary btn-block mt-3 h-14 gap-2 text-lg font-bold tap-target">
                        <Play size={20} /> Reprendre
                    </Link>
                    {/* Plus de raccourci d'abandon ici : il effaçait tout en
                        une frappe, sans confirmation, sur un écran qu'on touche
                        avec un gant. Le déroulé propose les deux sorties —
                        arrêter en gardant, ou abandonner — avec ce que chacune
                        fait du travail déjà fait. */}
                </section>
            )}

            {/* Un exercice ouvert hors séance : même raison, priorité à ce qui
                est commencé. */}
            {!run && session && (
                <Link to="/entrainement/exercices"
                    className="flex items-center gap-3 rounded-box border-2 border-primary bg-primary/10 p-4">
                    <Play size={24} className="shrink-0 text-primary" />
                    <span className="min-w-0 flex-1">
                        <span className="block font-bold">Exercice en cours</span>
                        <span className="block text-sm opacity-70">
                            Commencé le {dayLabel(session.startedAt)}. Reprendre la saisie.
                        </span>
                    </span>
                    <ChevronRight size={20} className="shrink-0 opacity-50" />
                </Link>
            )}

            {/* Les deux portes. Chacune porte un chiffre : de quoi savoir ce
                qu'il y a derrière sans avoir à l'ouvrir. */}
            <Link to="/entrainement/seances"
                className="flex items-center gap-4 rounded-box bg-base-200 p-5 transition-colors hover:bg-base-300">
                <ListChecks size={28} className="shrink-0 text-primary" />
                <span className="min-w-0 flex-1">
                    <span className="block text-lg font-bold">Séances</span>
                    <span className="mt-0.5 block text-sm opacity-60">
                        {workouts.length === 0
                            ? 'Préparez vos enchaînements d’exercices à l’avance.'
                            : `${workouts.length} séance${workouts.length > 1 ? 's' : ''} prête${workouts.length > 1 ? 's' : ''} à dérouler.`}
                    </span>
                </span>
                <ChevronRight size={20} className="shrink-0 opacity-50" />
            </Link>

            <Link to="/entrainement/exercices"
                className="flex items-center gap-4 rounded-box bg-base-200 p-5 transition-colors hover:bg-base-300">
                <Dumbbell size={28} className="shrink-0 text-primary" />
                <span className="min-w-0 flex-1">
                    <span className="block text-lg font-bold">Exercices</span>
                    <span className="mt-0.5 block text-sm opacity-60">
                        Lancer un exercice seul, ou composer les vôtres.
                    </span>
                </span>
                <ChevronRight size={20} className="shrink-0 opacity-50" />
            </Link>

            {/* Le putting n'est pas listé ici : c'est un exercice, il vit dans
                la liste des exercices avec les autres. Le remonter d'un niveau
                le ferait passer pour une troisième catégorie. */}

            <Link to="/entrainement/historique" className="btn btn-ghost btn-block gap-2">
                <BarChart3 size={18} /> Mes séances passées
            </Link>
        </div>
    );
}
